import type { Profile } from "./types";

type OfficialCategory = {
  gcid: string;
  category: string;
};

type RawCategorySuggestion = {
  gcid?: string;
  category: string;
  primary?: boolean;
};

export type CategorySuggestion = OfficialCategory & {
  primary: boolean;
};

export type KeywordDraft = {
  id: string;
  value: string;
  selected: boolean;
};

const normalizeText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const profileCityState = (profile?: Pick<Profile, "city" | "state">) => {
  const cityParts = (profile?.city || "").split(",").map((part) => part.trim());
  const city = cityParts[0] || "";
  const state = profile?.state || cityParts[1] || "";

  return {
    city,
    state: state.toUpperCase(),
  };
};

const uniqueByCategory = (categories: CategorySuggestion[]) => {
  const result: CategorySuggestion[] = [];

  categories.forEach((category) => {
    if (!result.some((item) => item.gcid === category.gcid)) {
      result.push(category);
    }
  });

  return result;
};

const contextTerms = (context: string) => {
  const normalized = normalizeText(context);
  const stopwords = new Set([
    "aqui",
    "cada",
    "chave",
    "cidade",
    "cliente",
    "compra",
    "comprar",
    "confirme",
    "empresa",
    "frente",
    "intencao",
    "mudar",
    "nome",
    "palavra",
    "palavras",
    "para",
    "preco",
    "produto",
    "produtos",
    "quero",
    "somente",
    "sugerida",
    "vende",
  ]);
  const terms = normalized
    .split(" ")
    .filter(
      (term) =>
        term.length > 2 &&
        !["com", "que", "uma"].includes(term) &&
        !stopwords.has(term),
    );

  const synonymMap: Record<string, string[]> = {
    pesca: ["pesca", "pescaria", "isca", "iscas", "pescador"],
    barco: ["barco", "barcos", "nautico", "nautica", "motor", "motores"],
    caca: ["caca", "arma", "armas", "municao"],
    esportiva: ["esportiva", "esportivo", "esporte"],
  };

  return Array.from(
    new Set([
      ...terms,
      ...terms.flatMap((term) => synonymMap[term] || []),
    ]),
  );
};

const scoreCategory = (category: OfficialCategory, terms: string[]) => {
  const normalizedCategory = normalizeText(category.category);

  return terms.reduce((score, term) => {
    if (normalizedCategory === term) return score + 8;
    if (normalizedCategory.includes(term)) return score + 3;
    return score;
  }, 0);
};

export const filterOfficialCategorySuggestions = ({
  suggestions,
  officialCategories,
  context,
  limit = 8,
}: {
  suggestions: RawCategorySuggestion[];
  officialCategories: OfficialCategory[];
  context: string;
  limit?: number;
}) => {
  const terms = contextTerms(context);
  const officialById = new Map(
    officialCategories.map((category) => [category.gcid, category]),
  );
  const officialByName = new Map(
    officialCategories.map((category) => [
      normalizeText(category.category),
      category,
    ]),
  );

  const exactSuggestions = suggestions
    .map((suggestion, index) => {
      const official =
        (suggestion.gcid && officialById.get(suggestion.gcid)) ||
        officialByName.get(normalizeText(suggestion.category));

      if (!official) return null;
      if (scoreCategory(official, terms) === 0) return null;

      return {
        ...official,
        primary: suggestion.primary ?? index === 0,
      };
    })
    .filter((category): category is CategorySuggestion => Boolean(category));

  const rankedSuggestions = officialCategories
    .map((category) => ({
      category,
      score: scoreCategory(category, terms),
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ category }, index) => ({
      ...category,
      primary: exactSuggestions.length === 0 && index === 0,
    }));

  return uniqueByCategory([...exactSuggestions, ...rankedSuggestions]).slice(
    0,
    limit,
  );
};

const instructionFragments = [
  "cidade dele",
  "cidade do cliente",
  "coloque o nome da cidade",
  "com intencao de compra",
  "confirme a cidade",
  "de cada palavra chave",
  "empresa que vende",
  "intencao de compra",
  "nome da cidade",
  "o cliente vende",
  "palavra chave sugerida",
  "palavras chave",
  "palavras chaves",
  "quero somente",
  "quero palavras",
];

const isInstructionKeyword = (keyword: string) => {
  const normalized = normalizeText(keyword);

  return (
    instructionFragments.some((fragment) => normalized.includes(fragment)) ||
    normalized.startsWith("quero ") ||
    normalized.startsWith("confirme ") ||
    normalized.startsWith("coloque ")
  );
};

const stripCityFromKeyword = (keyword: string, city: string, state: string) => {
  if (!city) return keyword;

  const cityPattern = escapeRegExp(city);
  const statePattern = state ? escapeRegExp(state) : "";
  const stateSuffix = statePattern ? `(\\s*[-,]?\\s*${statePattern})?` : "";

  return keyword
    .replace(
      new RegExp(`\\b(em|na|no|de|da|do|para)\\s+${cityPattern}${stateSuffix}\\b`, "gi"),
      "",
    )
    .replace(new RegExp(`\\b${cityPattern}${stateSuffix}\\b`, "gi"), "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();
};

export const sanitizeKeywordSuggestions = (
  keywords: string[],
  profile?: Pick<Profile, "city" | "state">,
) => {
  const { city, state } = profileCityState(profile);
  const sanitizedKeywords = keywords
    .map((keyword) => keyword.trim())
    .filter(Boolean)
    .filter((keyword) => !isInstructionKeyword(keyword))
    .map((keyword) => {
      if (!city) return stripCityFromKeyword(keyword, city, state);
      const cityRe = escapeRegExp(city);
      let cleaned = keyword
        .replace(new RegExp(`(^|\\s)(em|na|no|de|da|do|para)\\s+${cityRe}(?=\\s|$)`, "gi"), " ")
        .replace(new RegExp(`(^|\\s)${cityRe}(?=\\s|$)`, "gi"), " ")
        .replace(/\s+/g, " ")
        .trim();
      if (state) {
        const stateRe = escapeRegExp(state);
        cleaned = cleaned
          .replace(new RegExp(`(^|\\s)(em|na|no|de)\\s+${stateRe}(?=\\s|$)`, "gi"), " ")
          .replace(/\s+/g, " ")
          .trim();
      }
      if (normalizeText(cleaned).split(" ").filter(Boolean).length < 2) return "";
      return `${cleaned} em ${city}`;
    })
    .map((keyword) => keyword.replace(/\s+/g, " ").trim())
    .filter((keyword) => normalizeText(keyword).split(" ").length >= 3)
    .filter(
      (keyword, index, all) =>
        all.findIndex(
          (item) => normalizeText(item) === normalizeText(keyword),
        ) === index,
    );

  return sanitizedKeywords;
};

export const createKeywordDrafts = (
  keywords: string[],
  profile?: Pick<Profile, "city" | "state">,
): KeywordDraft[] =>
  sanitizeKeywordSuggestions(keywords, profile)
    .map((keyword, index) => ({
      id: `${normalizeText(keyword).replace(/\s/g, "-")}-${index}`,
      value: keyword,
      selected: true,
    }));

export const updateKeywordDraftValue = (
  drafts: KeywordDraft[],
  id: string,
  value: string,
) =>
  drafts.map((draft) =>
    draft.id === id ? { ...draft, value, selected: Boolean(value.trim()) } : draft,
  );

export const updateKeywordDraftSelection = (
  drafts: KeywordDraft[],
  id: string,
  selected: boolean,
) => drafts.map((draft) => (draft.id === id ? { ...draft, selected } : draft));

export const removeKeywordDraft = (drafts: KeywordDraft[], id: string) =>
  drafts.filter((draft) => draft.id !== id);

export const getSelectedKeywordValues = (drafts: KeywordDraft[]) =>
  drafts
    .filter((draft) => draft.selected)
    .map((draft) => draft.value.trim())
    .filter(Boolean);

const isInstructionLikeDescription = (description: string) => {
  const normalized = normalizeText(description);

  return (
    normalized.startsWith("otimize") ||
    normalized.startsWith("crie") ||
    normalized.startsWith("escreva") ||
    normalized.includes("seo local para")
  );
};

const joinLocation = (profile: Profile) =>
  [profile.city, profile.state].filter(Boolean).join(", ");

export const buildPublicProfileDescription = ({
  briefing,
  profile,
  keywords,
}: {
  briefing: string;
  profile: Profile;
  keywords: string[];
}) => {
  const cleanBriefing = briefing.trim();
  if (cleanBriefing && !isInstructionLikeDescription(cleanBriefing)) {
    return cleanBriefing;
  }

  const location = joinLocation(profile);
  const relevantTerms = keywords.slice(0, 4).join(", ");
  const specialty = relevantTerms || profile.category || "soluções locais";
  const locationText = location ? ` em ${location}` : "";

  return `${profile.name} é uma empresa${locationText} especializada em ${specialty}. Atende clientes da região com orientação sobre produtos, serviços e orçamento, conectando quem pesquisa no Google a uma solução local relacionada ao que deseja comprar ou contratar.`;
};

